import { useState, useEffect, useRef } from "react";
import { findNearestTrainStation } from "../data/trainStations";
import { resolveKnownApiCity } from "../utils/city";

const normalizeCityName = (city) =>
  `${city || ""}`
    .trim()
    .replace(/\s+(district|taluka)$/i, "");

const getGoogleAddressComponent = (components, type) =>
  components.find((item) => item.types?.includes(type))?.long_name;

const resolveDetectedCity = (...cities) => {
  for (const city of cities) {
    const normalizedCity = normalizeCityName(city);
    if (!normalizedCity) continue;

    const resolvedCity = resolveKnownApiCity(normalizedCity);
    if (resolvedCity) return resolvedCity;
  }

  return "Mumbai";
};

const buildFallbackLocationDetails = (latitude, longitude) => ({
  type: "Point",
  coordinates: [longitude, latitude],
  locality: null,
  city: "Mumbai",
  country: "India",
  stationName: null,
  fullAddress: "Unknown location",
});

export const useUserLocationState = () => {
  const [locationData, setLocationData] = useState(null);
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationStatus, setLocationStatus] = useState("loading");
  const [nearestTrainStation, setNearestTrainStation] = useState(null);
  const [permissionState, setPermissionState] = useState(null);
  const watchIdRef = useRef(null);

  const isGeolocationAvailable =
    typeof navigator !== "undefined" && !!navigator.geolocation;

  const getGoogleLocationDetails = async (latitude, longitude) => {
    try {
      const googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY;
      if (!googleApiKey) return null;

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${googleApiKey}`
      );

      if (!response.ok) return null;

      const data = await response.json();
      if (data.status !== "OK" || !data.results?.length) return null;

      const components = data.results[0]?.address_components || [];
      const locality =
        getGoogleAddressComponent(components, "locality") ||
        getGoogleAddressComponent(components, "sublocality") ||
        getGoogleAddressComponent(components, "administrative_area_level_3") ||
        getGoogleAddressComponent(components, "administrative_area_level_2") ||
        "Unknown";
      const city = resolveDetectedCity(
        getGoogleAddressComponent(components, "locality"),
        getGoogleAddressComponent(components, "administrative_area_level_2"),
        getGoogleAddressComponent(components, "administrative_area_level_1")
      );
      const country =
        getGoogleAddressComponent(components, "country") || "India";

      return {
        type: "Point",
        coordinates: [longitude, latitude],
        locality,
        city,
        country,
        stationName: null,
        fullAddress: data.results[0]?.formatted_address || "Unknown location",
      };
    } catch (googleError) {
      console.error("Google geocoding failed:", googleError);
      return null;
    }
  };

  const getNominatimLocationDetails = async (latitude, longitude) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&accept-language=en`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch location details");
    }

    const data = await response.json();
    const address = data.address || {};

    let stationName = null;

    if (
      data.category === "railway" ||
      data.type === "station" ||
      data.type === "halt"
    ) {
      stationName = data.name || null;
    }

    if (!stationName) {
      stationName =
        address.railway ||
        address.station ||
        address.public_transport ||
        null;
    }

    let locality =
      address.suburb ||
      address.neighbourhood ||
      address.locality ||
      address.village ||
      address.hamlet ||
      address.city_district ||
      address.district ||
      data.display_name?.split(",")[0] ||
      "Unknown";

    const cityCandidate = normalizeCityName(
      address.city ||
        address.town ||
        address.municipality ||
        address.city_district ||
        address.county ||
        address.state_district
    );
    let city = resolveDetectedCity(cityCandidate, locality, address.state);

    if (
      data.display_name &&
      data.display_name.toLowerCase().includes("mumbai")
    ) {
      city = "Mumbai";
    }

    return {
      type: "Point",
      coordinates: [longitude, latitude],
      locality,
      city,
      country: address.country || "India",
      stationName,
      fullAddress: data.display_name || "Unknown location",
    };
  };

  const getLocationDetails = async (latitude, longitude) => {
    const googleLocationDetails = await getGoogleLocationDetails(
      latitude,
      longitude
    );

    if (googleLocationDetails) {
      return googleLocationDetails;
    }

    try {
      return await getNominatimLocationDetails(latitude, longitude);
    } catch (nominatimError) {
      console.error("Nominatim geocoding failed:", nominatimError);
      return buildFallbackLocationDetails(latitude, longitude);
    }
  };

  const updatePermissionState = async () => {
    if (!isGeolocationAvailable || !navigator.permissions) {
      return null;
    }

    try {
      const status = await navigator.permissions.query({ name: "geolocation" });
      setPermissionState(status.state);
      status.onchange = () => setPermissionState(status.state);
      return status.state;
    } catch (err) {
      console.warn("Could not read geolocation permission state", err);
      return null;
    }
  };

  const resetLocationState = () => {
    setCoords(null);
    setLocationData(null);
    setNearestTrainStation(null);
  };

  const applyResolvedLocation = async (latitude, longitude) => {
    setCoords({ latitude, longitude });
    setPermissionState("granted");
    setLoading(false);

    try {
      const locationDetails = await getLocationDetails(latitude, longitude);
      setLocationData(locationDetails);

      const nearestStation = findNearestTrainStation(
        latitude,
        longitude,
        locationDetails.city
      );

      if (nearestStation) {
        setNearestTrainStation(nearestStation);
      }

      setLocationStatus("ready");
    } catch (err) {
      console.error("Failed to resolve location details:", err);
      setLocationData(buildFallbackLocationDetails(latitude, longitude));
      setLocationStatus("ready");
    }
  };

  const fetchUserLocation = ({
    permissionOverride = permissionState,
    forceRequest = false,
  } = {}) => {
    if (!isGeolocationAvailable) {
      setError("Geolocation is not available on this device");
      setLoading(false);
      setLocationStatus("unavailable");
      return;
    }

    if (!forceRequest && permissionOverride === "denied") {
      setError(
        "Location access is blocked in your browser. Please allow location permission from the browser settings and retry."
      );
      setLoading(false);
      setLocationStatus("denied");
      return;
    }

    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        applyResolvedLocation(
          position.coords.latitude,
          position.coords.longitude
        );
      },
      (geoError) => {
        setLoading(false);
        if (geoError?.code === 1) {
          setError(
            "Location access is blocked in your browser. Please allow location permission from the browser address bar or settings, then retry."
          );
          setPermissionState("denied");
          setLocationStatus("denied");
        } else {
          setError(geoError?.message || "Unable to access location");
          setLocationStatus("error");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: forceRequest ? 0 : 300000,
      }
    );
  };

  useEffect(() => {
    let cancelled = false;

    const initLocation = async () => {
      const permission = await updatePermissionState();
      if (cancelled) return;

      fetchUserLocation({ permissionOverride: permission });
    };

    initLocation();

    return () => {
      cancelled = true;
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  const refreshLocation = async () => {
    setLoading(true);
    setError(null);
    resetLocationState();
    setLocationStatus("loading");

    const permission = await updatePermissionState();

    fetchUserLocation({
      permissionOverride: permission,
      forceRequest: true,
    });
  };

  return {
    locationData,
    loading,
    error,
    refreshLocation,
    isGeolocationAvailable,
    isGeolocationEnabled: !!coords,
    coords,
    locationStatus,
    permissionState,
    nearestTrainStation,
  };
};

export default useUserLocationState;
